import { Grid, Input, Table } from '@design-systems/canvas-ui'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import {
  CAPABILITIES,
  CAPABILITY_NAME,
  KEY_CAPABILITY_ROW_DISPLAY_ITEM,
  KEY_SERVICE_ROW_DISPLAY_ITEM,
  MANUFACTURING_TYPE_ID_FABRIC,
  PROCESS_NAME,
  RFI_TYPE_LOCATION,
  SERVICE_CAPABILITIES,
  SERVICE_NAME,
} from './commonConstants'

/**
 * CheckboxTable component renders a table with checkboxes for selecting capabilities.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Array} props.capabilitiesList - The list of capabilities.
 * @param {Object} props.specifications - The specifications object containing options and display items.
 * @param {boolean} props.isReadOnly - Flag indicating if the table is read-only.
 * @param {number} props.manufacturingTypeId - The manufacturing type ID.
 * @param {string} props.rfiType - The RFI type.
 * @param {Function} props.updateCapabilitiesList - Function to update the capabilities list.
 * @returns {JSX.Element} The CheckboxTable component.
 */
const CheckboxTable = (props) => {
  const [capabilitiesList, setCapabilitiesList] = useState(
    props.capabilitiesList,
  )
  const [genericCapabilities, setGenericCapabilies] = useState([])
  const [serviceCapabilities, setServiceCapabilities] = useState([])

  //#region UseEffects

  useEffect(() => {
    if (props.capabilitiesList) {
      setCapabilitiesList(props.capabilitiesList)
      if (
        props.manufacturingTypeId === MANUFACTURING_TYPE_ID_FABRIC &&
        props.rfiType?.toLowerCase() === RFI_TYPE_LOCATION
      ) {
        setGenericCapabilies(
          props.capabilitiesList.filter((c) => c.type === PROCESS_NAME),
        )
      } else {
        setGenericCapabilies(
          props.capabilitiesList.filter((c) => c.type === CAPABILITY_NAME),
        )
      }
      setServiceCapabilities(
        props.capabilitiesList.filter((c) => c.type === SERVICE_NAME),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.capabilitiesList])

  useEffect(() => {
    if (
      !_.isEqual(capabilitiesList, props.capabilitiesList) &&
      !props.isReadOnly
    ) {
      props.updateCapabilitiesList(capabilitiesList)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capabilitiesList])

  useEffect(() => {
    if (!props.onCountsChange) return

    const options = props.specifications?.options || []
    const optionValues = options.map((o) => o.value)
    const targetValue =
      options.find((o) =>
        String(o?.label || '')
          .toLowerCase()
          .includes('target'),
      )?.value ?? 0
    const vendorValue =
      options.find((o) =>
        String(o?.label || '')
          .toLowerCase()
          .includes('vendor'),
      )?.value ?? 1
    const gmiValue =
      options.find((o) =>
        String(o?.label || '')
          .toLowerCase()
          .includes('gmi'),
      )?.value ?? 2

    const capabilityTypeUid =
      props.specifications?.capabilityRowDisplayItems?.capabilityTypeUid
    const serviceTypeUid =
      props.specifications?.serviceRowDisplayItems?.capabilityTypeUid

    const includedTypes = [
      capabilityTypeUid,
      ...(props.specifications?.showServiceCapabilities
        ? [serviceTypeUid]
        : []),
    ].filter(Boolean)

    const getCountByTypeAndOption = (typeUid, optionValue) =>
      capabilitiesList?.filter(
        (eachCapability) =>
          eachCapability.type === typeUid &&
          eachCapability.vetting_status?.includes(optionValue),
      )?.length || 0

    const optionCountMap = Object.fromEntries(
      optionValues.map((optionValue) => [
        optionValue,
        includedTypes.reduce(
          (total, typeUid) =>
            total + getCountByTypeAndOption(typeUid, optionValue),
          0,
        ),
      ]),
    )

    const targetDesignatedCount = optionCountMap[targetValue] || 0
    const vendorSourcedCount = optionCountMap[vendorValue] || 0
    const gmiCertifiedCount = optionCountMap[gmiValue] || 0

    const totalSelectedCount = Object.values(optionCountMap).reduce(
      (sum, count) => sum + count,
      0,
    )

    props.onCountsChange({
      totalSelectedCount,
      targetDesignatedCount,
      vendorSourcedCount,
      gmiCertifiedCount,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capabilitiesList, props.onCountsChange, props.specifications])

  //#endregion

  /**
   * Handle checkbox change
   * @param {*} event
   * @param {*} capability
   * @param {*} sourcedBy - 0 for Target designated (Nominated) and 1 for vendor sourced
   * @param {*} selectAll
   * @param {*} capabilitiesToBeUpdated
   */
  const handleCheckboxEventChange = (
    event,
    capability,
    sourcedBy,
    selectAll,
    capabilitiesToBeUpdated,
  ) => {
    event.stopPropagation()

    let updatedCapabilityList = [...capabilitiesList]

    if (event.target.checked) {
      if (selectAll) {
        capabilitiesToBeUpdated = capabilitiesToBeUpdated?.map((c) =>
          c.vetting_status?.includes(sourcedBy)
            ? c
            : addElementToVettingList(c, sourcedBy),
        )
        updateAllCapabilitiesList(
          updatedCapabilityList,
          capabilitiesToBeUpdated,
        )
      } else {
        updatedCapabilityList = updatedCapabilityList?.map((c) =>
          c.primary_type === capability.primary_type &&
          c?.sub_type === capability?.sub_type
            ? addElementToVettingList(c, sourcedBy)
            : c,
        )
      }
    } else {
      if (selectAll) {
        capabilitiesToBeUpdated = capabilitiesToBeUpdated?.map((c) =>
          c.vetting_status?.includes(sourcedBy)
            ? deleteElementFromVettingList(c, sourcedBy)
            : c,
        )
        updateAllCapabilitiesList(
          updatedCapabilityList,
          capabilitiesToBeUpdated,
        )
      } else {
        updatedCapabilityList = updatedCapabilityList?.map((c) =>
          c.primary_type === capability.primary_type &&
          c?.sub_type === capability?.sub_type
            ? deleteElementFromVettingList(c, sourcedBy)
            : c,
        )
      }
    }

    setCapabilitiesList(updatedCapabilityList)
  }

  /**
   * Update all capabilities based on type
   * @param {*} updatedCapabilityList
   * @param {*} capabilitiesToBeUpdated
   * @returns
   */
  const updateAllCapabilitiesList = (
    updatedCapabilityList,
    capabilitiesToBeUpdated,
  ) => {
    updatedCapabilityList?.forEach((capability) => {
      let updatedCapability = capabilitiesToBeUpdated?.find(
        (c) =>
          c.primary_type === capability.primary_type &&
          c?.sub_type === capability?.sub_type,
      )
      if (updatedCapability) {
        capability.vetting_status = updatedCapability.vetting_status
      }
    })
  }

  /**
   * Add capability number to vetting status list of product capability
   * @param {*} capability
   * @param {*} sourcedBy
   * @returns
   */
  const addElementToVettingList = (capability, sourcedBy) => {
    return {
      ...capability,
      vetting_status: capability.vetting_status
        ? [...capability.vetting_status, sourcedBy]
        : [sourcedBy],
    }
  }

  /**
   * Remove capability number from vetting status list of product capability
   * @param {*} capability
   * @param {*} sourcedBy - 0 for Target designated (Nominated) and 1 for vendor sourced
   */
  const deleteElementFromVettingList = (capability, sourcedBy) => {
    return {
      ...capability,
      vetting_status: [...capability.vetting_status].filter(
        (n) => n !== sourcedBy,
      ),
    }
  }

  /**
   * Gets the count of columns based on the specified type and vetting status value.
   * @param {string} type - The type of capability to filter by.
   * @param {string} value - The vetting status value to filter by.
   * @returns {number} The count of columns that match the specified type and vetting status value.
   */
  const getColumnCount = (type, value) => {
    return capabilitiesList?.filter(
      (eachCapability) =>
        eachCapability.type === type &&
        eachCapability.vetting_status?.includes(value),
    )?.length
  }

  /**
   * Creates a Canvas v8 Table with checkboxes for selecting capabilities.
   *
   * @param {string} header - The header title for the table.
   * @param {Array} capabilities - The list of capabilities to be displayed in the table.
   * @param {string} capabilityTypeUid - The unique identifier for the capability type.
   * @returns {JSX.Element} The table element with checkboxes.
   */
  const createCheckboxTable = (header, capabilities, capabilityTypeUid) => {
    const options = props.specifications?.options || []
    const kebabHeader = _.kebabCase(header)
    const displayItemKey =
      header === CAPABILITIES
        ? KEY_CAPABILITY_ROW_DISPLAY_ITEM
        : KEY_SERVICE_ROW_DISPLAY_ITEM
    const showPrimaryType =
      props.specifications[displayItemKey]?.showPrimaryType
    const showSubType = props.specifications[displayItemKey]?.showSubType

    // Fixed first column: capability label
    const columnDefs = [
      {
        id: 'capabilityLabel',
        header: header,
        isRowHeader: true,
        cell: ({ row }) => {
          const capability = row.original.__capability
          return (
            <>
              {showPrimaryType && capability.primary_type}
              <strong>{showSubType && ` - ${capability.sub_type}`}</strong>
            </>
          )
        },
      },
      // Dynamic option columns with "select all" checkbox in header
      ...options.map((column, colIdx) => ({
        id: `option-${colIdx}`,
        header: () => (
          <Input.Checkbox
            id={`${_.kebabCase(column.label)}-select-all-${kebabHeader}`}
            data-testid={`${_.kebabCase(column.label)}-select-all-${kebabHeader}`}
            label={`${column.label} (${getColumnCount(capabilityTypeUid, column.value)})`}
            className={props.isReadOnly ? 'click-disable' : ''}
            isDisabled={!props.isReadOnly && !capabilities?.length}
            isChecked={
              capabilities?.length &&
              capabilities.every((c) =>
                c.vetting_status?.includes(column.value),
              )
                ? true
                : false
            }
            onChange={(event) => {
              handleCheckboxEventChange(
                event,
                null,
                column.value,
                true,
                capabilities,
              )
            }}
          />
        ),
        cell: ({ row }) => {
          const capability = row.original.__capability
          const capabilityIndex = row.index
          return (
            <Input.Checkbox
              id={`${kebabHeader}-row-${capabilityIndex}-${colIdx}`}
              data-testid={`td-td-1-${capabilityIndex}-${kebabHeader}`}
              className={props.isReadOnly ? 'click-disable' : ''}
              isChecked={
                capability.vetting_status &&
                capability.vetting_status?.includes(column.value)
                  ? true
                  : false
              }
              onChange={(event) => {
                handleCheckboxEventChange(event, capability, column.value)
              }}
            />
          )
        },
      })),
    ]

    // Build rowData; keep the original capability accessible on each row
    const rowData = (capabilities || []).map((capability, index) => ({
      id: `${kebabHeader}-tr-${index}`,
      __capability: capability,
    }))

    return (
      <Table
        name={`Product capabilities table - ${header}`}
        data-testid={`product-capability-table-${kebabHeader}`}
        key={`product-capability-table-${header}`}
        cellPadding="dense"
        hasGrid
        emptyMessage="Capabilities not available"
        data={{
          columnDefs,
          rowData,
        }}
      />
    )
  }

  return (
    <Grid.Container>
      <Grid.Item size={{ xs: 12 }}>
        {/* a. Capability/Process selections */}
        {createCheckboxTable(
          CAPABILITIES,
          genericCapabilities,
          props.specifications?.capabilityRowDisplayItems?.capabilityTypeUid,
        )}
      </Grid.Item>

      {/* b. Service Capability selections */}
      {props.specifications?.showServiceCapabilities && (
        <Grid.Item size={{ xs: 12 }} className="hc8-pt-6x">
          {createCheckboxTable(
            SERVICE_CAPABILITIES,
            serviceCapabilities,
            props.specifications?.serviceRowDisplayItems?.capabilityTypeUid,
          )}
        </Grid.Item>
      )}
    </Grid.Container>
  )
}

export default CheckboxTable
